import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../app/store/slices/uiSlice";
import CRUDDetailView from "./CRUDDetailView";
import { DetailPageSkeleton } from "../ui";
import apiService from "../../services/apiService";

/**
 * CRUDDetailPage - URL-based detail/edit page component
 * Handles view and edit modes with ID from URL params
 */
export default function CRUDDetailPage({ config, service }) {
  const { id, parentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const darkMode = useSelector(selectDarkMode);

  const { title, formFields: rawFormFields = [], customHandlers = {}, basePath: configBasePath } = config;
  const formFields = Array.isArray(rawFormFields) ? rawFormFields : [];

  // Determine mode from URL path
  const isEditMode = location.pathname.includes("/edit/");
  const isAddMode = location.pathname.includes("/add");

  const [viewState, setViewState] = useState({
    mode: isAddMode ? "add" : isEditMode ? "edit" : "view",
    item: null,
    initialData: parentId ? { parentId } : {},
  });
  const [fetchError, setFetchError] = useState(null);
  const [loading, setLoading] = useState(!isAddMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  // Initialize activeTab from navigation state (for redirect from add to edit) or config
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || config.formState?.activeTab || "basic");
  const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, item: null });
  const [transferId, setTransferId] = useState(null); // For multi-step form submission

  // Update mode when URL changes (e.g., navigating from view to edit)
  useEffect(() => {
    const newMode = isAddMode ? "add" : isEditMode ? "edit" : "view";
    setViewState((prev) => ({ ...prev, mode: newMode }));
  }, [isEditMode, isAddMode]);

  // Fetch item data by ID
  useEffect(() => {
    let cancelled = false;

    const fetchItem = async () => {
      if (!id || isAddMode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let itemData;

        // Use custom onView handler if available, otherwise use service
        if (customHandlers?.onView) {
          itemData = await customHandlers.onView({ id });
        } else if (service?.getById) {
          const res = await service.getById(id);
          itemData = res?.data?.data || res?.data || res;
        } else {
          // Fallback to apiService
          const res = await apiService.getById(config.endpoint, id);
          itemData = res?.data?.data || res?.data || res;
        }

        // Only update state if not cancelled (prevents double fetch in StrictMode)
        if (!cancelled) {
          setViewState((prev) => ({ ...prev, item: itemData }));
          // Set transferId from loaded item for edit mode (multi-step submission)
          if (itemData?.id || itemData?._id) {
            setTransferId(itemData.id || itemData._id);
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching item:", error);
          setFetchError(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchItem();

    // Cleanup: cancel pending updates when effect re-runs
    return () => {
      cancelled = true;
    };
  }, [id, isAddMode]);

  // Handle back to list
  const handleBackToList = () => {
    // Navigate back to the list page
    // For add mode (/admin/xxx/add), use -1 to remove just "add"
    // For view/edit mode (/admin/xxx/view/id), use -2 to remove "view/id" or "edit/id"
    const sliceCount = isAddMode ? -1 : -2;
    const basePath = location.pathname.split("/").slice(0, sliceCount).join("/");
    navigate(basePath || configBasePath || "/admin/dashboard");
  };

  // Handle edit from view mode
  const handleEditFromView = () => {
    const basePath = location.pathname.replace("/view/", "/edit/");
    navigate(basePath);
  };

  // Handle redirect to edit mode after creating a transfer in add mode
  // This ensures data persists on page reload
  const handleRedirectToEdit = (newTransferId, nextTab) => {
    // Get base path (remove /add)
    const basePath = location.pathname.replace("/add", "");
    // Navigate to edit page with the new transferId and preserve the active tab
    navigate(`${basePath}/edit/${newTransferId}`, {
      state: { activeTab: nextTab },
    });
  };

  // Handle form submit (receives values from Formik)
  const handleSubmit = async (formValues) => {
    setIsSubmitting(true);

    try {
      // Handle multi-step form submission (final tab - inspection)
      if (config.multiStepSubmission && customHandlers?.onTabSubmit && activeTab === "inspection") {
        const result = await customHandlers.onTabSubmit(
          activeTab,
          null, // No next tab, it's the final submission
          formValues,
          viewState.mode,
          transferId,
        );

        if (result?.success) {
          // Navigate immediately with success message
          const pathParts = location.pathname.split("/");
          const sliceCount = viewState.mode === "add" ? -1 : -2;
          const basePath = pathParts.slice(0, sliceCount).join("/");
          const successMessage = `${title} ${viewState.mode === "add" ? "added" : "updated"} successfully!`;
          navigate(basePath || configBasePath || "/admin/dashboard", {
            state: { refresh: true, timestamp: Date.now(), successMessage },
          });
          return;
        }
      }

      // For non-multi-step forms or other tabs, use regular submit
      const processedData = customHandlers?.processFormData ? await customHandlers.processFormData(formValues, viewState.mode) : formValues;

      if (viewState.mode === "add") {
        if (service?.create) {
          await service.create(processedData);
        } else {
          await apiService.create(config.endpoint, processedData);
        }
      } else if (viewState.mode === "edit") {
        if (service?.update) {
          await service.update(id, processedData);
        } else {
          await apiService.update(config.endpoint, id, processedData);
        }
      }

      // Navigate immediately with success message
      const pathParts = location.pathname.split("/");
      const sliceCount = viewState.mode === "add" ? -1 : -2;
      const basePath = pathParts.slice(0, sliceCount).join("/");
      const successMessage = `${title} ${viewState.mode === "add" ? "added" : "updated"} successfully!`;
      navigate(basePath || configBasePath || "/admin/dashboard", {
        state: { refresh: true, timestamp: Date.now(), successMessage },
      });
    } catch (error) {
      setIsSubmitting(false);
      // Extract and show error message
      const errorMessage = error?.response?.data?.message || error?.message || "An error occurred. Please try again.";
      setToast({ show: true, message: errorMessage, type: "error" });
      setTimeout(() => {
        setToast({ show: false, message: "", type: "error" });
      }, 5000);
    }
  };

  // Handle cancel edit - always go back to main list
  const handleCancelEdit = () => {
    handleBackToList();
  };

  // Handle delete
  const handleDelete = () => {
    setConfirmationModal({ isOpen: true, item: viewState.item });
  };

  const confirmDelete = async () => {
    if (!viewState.item) return;

    setIsDeleting(true);
    try {
      if (service?.delete) {
        await service.delete(viewState.item.id);
      } else {
        await apiService.delete(config.endpoint, viewState.item.id);
      }
      setConfirmationModal({ isOpen: false, item: null });
      // Navigate immediately with success message
      const basePath = location.pathname.split("/").slice(0, -2).join("/");
      navigate(basePath || configBasePath || "/admin/dashboard", {
        state: { refresh: true, timestamp: Date.now(), successMessage: `${title} deleted successfully!` },
      });
    } catch (error) {
      setConfirmationModal({ isOpen: false, item: null });
      setIsDeleting(false);
      setToast({ show: true, message: error.message || "Failed to delete", type: "error" });
      setTimeout(() => {
        setToast({ show: false, message: "", type: "error" });
      }, 3000);
    }
  };

  // Get display name
  const getDisplayName = (item) => {
    if (!item) return "";
    return item.name || item.centerName || item.vaccineName || item.batchNumber || item.fullName || `${title} #${item.id}`;
  };

  // Loading state
  if (loading) {
    return <DetailPageSkeleton fieldsCount={formFields.length || 9} showImage={formFields.some((f) => f.type === "file" || f.name === "profilePicture")} columns={3} />;
  }

  // Error state
  if (fetchError) {
    return (
      <div className="p-6">
        <div className={`p-4 rounded-xl border ${darkMode ? "bg-red-900/20 border-red-800 text-red-400" : "bg-red-50 border-red-200 text-red-600"}`}>
          <p className="font-medium">Error loading {title.toLowerCase()}</p>
          <p className="text-sm mt-1 opacity-80">{fetchError}</p>
          <button onClick={handleBackToList} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <CRUDDetailView
      title={title}
      viewState={viewState}
      formFields={formFields}
      isSubmitting={isSubmitting}
      isDeleting={isDeleting}
      toast={toast}
      customHandlers={customHandlers}
      activeTab={activeTab}
      onBackToList={handleBackToList}
      onEditFromView={handleEditFromView}
      onDelete={handleDelete}
      onSubmit={handleSubmit}
      onCancel={handleCancelEdit}
      setActiveTab={setActiveTab}
      confirmationModal={confirmationModal}
      onConfirmDelete={confirmDelete}
      onCancelDelete={() => setConfirmationModal({ isOpen: false, item: null })}
      getDisplayName={getDisplayName}
      transferId={transferId}
      setTransferId={setTransferId}
      multiStepSubmission={config.multiStepSubmission}
      onRedirectToEdit={handleRedirectToEdit}
      config={config}
    />
  );
}
